import {json, redirect, type LoaderArgs} from '@shopify/remix-oxygen';
import {useLoaderData} from '@remix-run/react';
import {CACHE_LONG, routeHeaders} from '~/data/cache';
import {blogPagination, BLOG_PAGE_SIZE} from '~/lib/blogPagination';
import {blogListingSeo} from '~/lib/blogSeo.server';
import type {LinksFunction} from '@shopify/remix-oxygen';
//domains
import {
  getLangagueCodeForSanityQuery,
  getCountryCodeForSanityQuery,
} from '~/domains/sanity';
import {useBlogQuery as fetchBlogs} from 'domains/blog';

//components
import BlogListing from '~/components/blogListing';

//translation
import translation from 'translations/blog.json';
import {routeLocale} from 'domains/global';

export const headers = routeHeaders;

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css',
    },
  ];
};

export async function loader({request, params, context}: LoaderArgs) {
  const {page, start, end, redirectTo} = blogPagination(request.url);
  if (redirectTo) return redirect(redirectTo, 301);

  const {blogs, recentBlogs, tags, totalBlogs, categories, blogSettings} =
    await fetchBlogs({
      languageCode: getLangagueCodeForSanityQuery(
        context.storefront.i18n.language,
      ),
      countryCode: getCountryCodeForSanityQuery(
        context.storefront.i18n.country,
      ),
      start,
      end,
    });
  // An empty first page is valid; later empty pages must not become soft 404s.
  if (page > 1 && blogs.length === 0) {
    throw new Response(null, {status: 404});
  }
  const {seo, heading, description} = blogListingSeo({
    requestUrl: request.url,
    english: context.storefront.i18n.language === 'EN',
    page,
    blogs,
  });

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  return json(
    {
      translation: translation[selectedLocale],
      seo,
      heading,
      description,
      page,
      pageCount: Math.ceil(totalBlogs / BLOG_PAGE_SIZE),
      blogs,
      recentBlogs,
      tags,
      totalBlogs,
      categories,
      blogSettings,
      currentTag: null,
      currentCategory: null,
    },
    {
      headers: {
        'Cache-Control': CACHE_LONG,
      },
    },
  );
}

export default function Page() {
  const data = useLoaderData<typeof loader>();
  return <BlogListing {...data} />;
}
